import { stampShort } from './network/format'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  get,
  hostError,
  hostsApi,
  type Host,
  type HostCommand,
  type HostCommandKind,
  type HostCreateRequest,
  type HostNetwork,
  type HostStatus,
  type InstallInfo,
  type InstallJob,
  type InstallStage,
  type NodeEgress,
  type NodeInfo,
  type NodeRole,
  type ProvisioningKeys,
  type RebootPlan,
  type RebootWatch,
  type LiveCount,
  type NetWarning,
} from '../api'
import { useNow } from '../hooks'
import { useSession } from '../session'
import { Button, Field, Notice, Tally, ago, type Lamp } from '../components/ui'
import { Button as SxButton } from '../ui'
import { Segmented } from '../live-ui/ui'
import { RestartNodeDialog } from './RestartNodeDialog'
import { freeFirst, outcome, serviceOf, switchBlock, SwitchOutcome, SwitchToast, switchDone } from './network/nodeSwitch'
import { publishersOn } from './network/updateGate'
import { FactorBadge, FactorCancelled, useSecondFactor } from './SecondFactor'
import {
  COMMAND_LABEL,
  COMMAND_STATE_TEXT,
  CONFIRM_WORD,
  FINGERPRINT_RE,
  HOSTNAME_RE,
  HOSTS_KEY,
  MAX_PACKAGES,
  NET_WARNING_TEXT,
  NO_FACTOR,
  PACKAGE_RE,
  REBOOT_CHECK_TEXT,
  REBOOT_DELAYS,
  REBOOT_STATE_TEXT,
  REGION_RE,
  SLUG_RE,
  SSH_USER_RE,
  STAGE_INFO,
  STAGE_STATUS_TEXT,
  STAGE_WAIT,
  NODES_KEY,
  TOTP_KEY,
  addressProblem,
  clock,
  dnsProblem,
  hostErrorText,
  hostJobsKey,
  hostKey,
  jobKey,
  liveText,
  netWarnings,
  releasesBehind,
  stageDuration,
  stagesWithRelayKey,
  tail4k,
  usePollWhileDown,
  withCommand,
} from './provisioning'

/*
 * Node provisioning in the console (docs/m2-provisioning.md §11): the
 * center's public key and the install form, the install's stages, and the
 * host of a media node with its agent, release and commands.
 */

const ROLE_OPTS: { value: NodeRole; label: string }[] = [
  { value: 'edge', label: 'Edge' },
  { value: 'hub', label: 'Hub' },
]
const EGRESS_OPTS: { value: NodeEgress; label: string }[] = [
  { value: 'direct', label: 'Direct' },
  { value: 'via_center', label: 'Via center' },
]

/** Lets a failed request through to the caller unless the admin just closed the code window. */
const cancelled = (e: unknown) => e instanceof FactorCancelled

function Mono({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <span data-testid={testId} className="font-mono text-xs break-all">
      {children}
    </span>
  )
}

/** A collapsed block of remote output (monospace, at most 4 KB). */
function OutputBlock({ title, text, testId, open }: { title: string; text: string; testId?: string; open?: boolean }) {
  return (
    <details data-testid={testId} open={open} className="rounded-md border border-line bg-bg">
      <summary className="cursor-pointer px-3 py-1.5 text-xs text-muted select-none hover:text-text">{title}</summary>
      <pre className="max-h-72 overflow-auto border-t border-line px-3 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all text-text-soft">
        {tail4k(text)}
      </pre>
    </details>
  )
}

// ---------------------------------------------------------------------------
// The center's key and the install form
// ---------------------------------------------------------------------------

/** "Public key of the center": the line for authorized_keys with one-click copy. */
export function CenterKey({ keys }: { keys: ProvisioningKeys }) {
  const [copied, setCopied] = useState(false)
  return (
    <section data-testid="prov-key" className="space-y-2 rounded-md border border-line bg-bg/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Public key of the center</h3>
        <Button
          data-testid="prov-key-copy"
          onClick={async () => {
            await navigator.clipboard?.writeText(keys.ssh_authorized_key).catch(() => undefined)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
        >
          {copied ? 'Copied' : 'Copy key'}
        </Button>
      </div>
      <code data-testid="prov-key-line" className="block rounded-md border border-line bg-bg px-3 py-2 font-mono text-[11px] break-all text-text">
        {keys.ssh_authorized_key}
      </code>
      <p className="text-xs text-text-soft">
        Add this line to <span className="font-mono">~/.ssh/authorized_keys</span> of the SSH user on the server (root, or a user with{' '}
        <span className="font-mono">sudo</span> without a password). The center logs in with it to install the node.
      </p>
      <p className="text-xs text-muted">
        Key fingerprint: <Mono testId="prov-key-fp">{keys.ssh_fingerprint}</Mono>
      </p>
    </section>
  )
}

interface FormState {
  address: string
  ssh_port: string
  ssh_user: string
  ssh_host_key_sha256: string
  slug: string
  hostname: string
  region: string
  role: NodeRole
  egress: NodeEgress
  keep_ssh_access: boolean
  reboot: RebootChoice
  reboot_delay: string
  /** join the routing network (R0): default on when the center's relay is on (PROV-RELAY-1) */
  relay: boolean
}

/** Reboot after an install or an update, as the console offers it. */
type RebootChoice = 'none' | 'now' | 'later'
const REBOOT_OPTS: { value: RebootChoice; label: string }[] = [
  { value: 'none', label: 'No' },
  { value: 'now', label: 'Right away' },
  { value: 'later', label: 'Later' },
]

/** The plan the API takes; undefined when the delay is not a number of minutes. */
export function rebootPlan(choice: RebootChoice, delay: string): RebootPlan | undefined {
  if (choice === 'none') return 'none'
  if (choice === 'now') return 'now'
  const n = Number(delay)
  return Number.isInteger(n) && n >= 1 && n <= 1440 ? { delay_minutes: n } : undefined
}

const EMPTY: FormState = {
  address: '',
  ssh_port: '22',
  ssh_user: '',
  ssh_host_key_sha256: '',
  slug: '',
  hostname: '',
  region: '',
  role: 'edge',
  egress: 'direct',
  keep_ssh_access: true,
  reboot: 'none',
  reboot_delay: '30',
  relay: false,
}

/** Field errors of the install form ('' = fine). */
export function formErrors(f: FormState, zone: string) {
  const port = Number(f.ssh_port)
  return {
    address: addressProblem(f.address),
    ssh_port: Number.isInteger(port) && port >= 1 && port <= 65535 ? '' : 'A port from 1 to 65535',
    ssh_user: SSH_USER_RE.test(f.ssh_user.trim()) ? '' : 'A Linux user name, e.g. root or restream',
    ssh_host_key_sha256: !f.ssh_host_key_sha256.trim() || FINGERPRINT_RE.test(f.ssh_host_key_sha256.trim()) ? '' : 'Looks like SHA256: and 43 characters',
    slug: SLUG_RE.test(f.slug.trim()) ? '' : 'Lowercase letters, digits and dashes, up to 31; starts with a letter or digit',
    hostname: zone || HOSTNAME_RE.test(f.hostname.trim()) ? '' : 'The DNS name already pointing at the address, e.g. fra1.ingest.example.com',
    region: REGION_RE.test(f.region.trim()) ? '' : 'Lowercase letters, digits and dashes, up to 32',
    reboot_delay: rebootPlan(f.reboot, f.reboot_delay) ? '' : 'Minutes, 1 to 1440',
  }
}

/**
 * The install form (POST /api/admin/hosts). keys is the answer of GET
 * /api/admin/provisioning/keys; onStarted gets the 202 job.
 */
export function InstallForm({ keys, onStarted, onCancel }: { keys: ProvisioningKeys; onStarted: (j: InstallJob) => void; onCancel: () => void }) {
  const qc = useQueryClient()
  const factor = useSecondFactor()
  const info = keys.install
  const zone = info?.dns_zone ?? ''
  const centerRelay = keys.relay_enabled === true
  const [f, setF] = useState<FormState>({ ...EMPTY, relay: centerRelay })
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const errors = formErrors(f, zone)
  const valid = !Object.values(errors).some(Boolean)
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((x) => ({ ...x, [k]: v }))
  const slug = f.slug.trim()
  const dns = dnsProblem(info)

  const submit = async () => {
    setTouched(true)
    if (!valid || busy) return
    setBusy(true)
    setErr('')
    const body: HostCreateRequest = {
      address: f.address.trim(),
      ssh_port: Number(f.ssh_port),
      ssh_user: f.ssh_user.trim(),
      slug,
      region: f.region.trim(),
      role: f.role,
      egress: f.egress,
      keep_ssh_access: f.keep_ssh_access,
      relay: f.relay,
    }
    const plan = rebootPlan(f.reboot, f.reboot_delay)
    if (plan && plan !== 'none') body.reboot = plan
    if (f.ssh_host_key_sha256.trim()) body.ssh_host_key_sha256 = f.ssh_host_key_sha256.trim()
    if (!zone) body.hostname = f.hostname.trim()
    try {
      const job = await factor((totp) => hostsApi.create(totp ? { ...body, totp } : body))
      qc.setQueryData(jobKey(job.id), job)
      void qc.invalidateQueries({ queryKey: HOSTS_KEY })
      void qc.invalidateQueries({ queryKey: TOTP_KEY })
      onStarted(job)
    } catch (e) {
      if (!cancelled(e)) setErr(hostErrorText(e))
    } finally {
      setBusy(false)
    }
  }

  const field = (k: keyof FormState & keyof ReturnType<typeof formErrors>, label: string, extra: { placeholder?: string; hint?: ReactNode; inputMode?: 'numeric' | 'text' } = {}) => (
    <Field
      label={label}
      name={`host-${k}`}
      data-testid={`host-${k}`}
      value={f[k] as string}
      autoComplete="off"
      spellCheck={false}
      error={touched ? errors[k] : undefined}
      onChange={(e) => set(k, e.target.value)}
      {...extra}
    />
  )

  return (
    <form
      className="space-y-4"
      data-testid="host-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <CenterKey keys={keys} />
      {dns && (
        <p data-testid="prov-dns-problem" className="rounded-md border border-warn/50 bg-warn/[.07] px-3 py-2 text-xs text-warn">
          ▲ {dns}
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.4fr)]">
        {field('address', 'Server address', { placeholder: '51.15.20.30', hint: 'Public IPv4' })}
        {field('ssh_port', 'SSH port', { inputMode: 'numeric' })}
        {field('ssh_user', 'SSH user', { placeholder: 'root', hint: 'root or sudo without a password' })}
      </div>

      <div>
        {field('ssh_host_key_sha256', 'Host key fingerprint (optional)', {
          placeholder: 'SHA256:…',
          hint: (
            <>
              From the hosting console: <span className="font-mono">ssh-keygen -lf /etc/ssh/ssh_host_ed25519_key.pub</span>
            </>
          ),
        })}
        {!f.ssh_host_key_sha256.trim() && (
          <p data-testid="host-tofu-warn" className="mt-2 rounded-md border border-warn/50 bg-warn/[.07] px-3 py-2 text-xs text-warn">
            ▲ Without a fingerprint the center accepts the key the server shows on the first connection (trust on first use) and pins it. Someone between the
            center and the server at that moment could pass for it. Compare the fingerprint shown in the progress with the hosting console.
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          {field('slug', 'Node name', { placeholder: 'fra2', hint: 'Also the media node name' })}
          {zone && (
            <p className="mt-1 text-xs text-muted">
              DNS name: <Mono testId="host-name-preview">{`${slug || '<name>'}.${zone}`}</Mono>
            </p>
          )}
        </div>
        {field('region', 'Region', { placeholder: 'eu-central', hint: 'Users pick it as their ingest region' })}
      </div>
      {!zone && field('hostname', 'Server DNS name', { placeholder: 'fra2.ingest.example.com', hint: 'No DNS zone is set up on the center: the name must already point at the address' })}

      <div className="grid gap-3 sm:grid-cols-2">
        <Segmented label="Role" options={ROLE_OPTS} value={f.role} onChange={(v) => set('role', v)} />
        <Segmented label="Egress" options={EGRESS_OPTS} value={f.egress} onChange={(v) => set('egress', v)} />
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="host-relay"
          checked={f.relay}
          onChange={(e) => set('relay', e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[var(--live)]"
        />
        <span>
          Routing network (relay)
          <span className="block text-xs text-muted" data-testid="host-relay-hint">
            {f.relay
              ? 'The node registers its relay key when it joins and every node of the network trusts it: it can carry routes as soon as the install ends.'
              : 'The node is installed outside the routing network. You can turn the relay on later from the node card (Enable relay), without a rebind.'}
            {!centerRelay && f.relay && ' The center’s relay is off (RELAY_ENABLED): the node joins the network once it is on.'}
          </span>
        </span>
      </label>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="host-keep-ssh"
          checked={f.keep_ssh_access}
          onChange={(e) => set('keep_ssh_access', e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[var(--live)]"
        />
        <span>
          Keep the center's SSH access
          <span className="block text-xs text-muted">
            SSH stays a backup channel; you can revoke it later. Unchecked, the key is removed at the end and the center checks that it can no longer log in.
          </span>
        </span>
      </label>

      <div className="grid gap-3 sm:grid-cols-2" data-testid="host-reboot-plan">
        <Segmented label="Reboot after the install" options={REBOOT_OPTS} value={f.reboot} onChange={(v) => set('reboot', v)} />
        {f.reboot === 'later' &&
          field('reboot_delay', 'Minutes after the node is up', { inputMode: 'numeric', hint: 'Settings that need a reboot (NIC queues, qdisc) take effect then' })}
        {f.reboot === 'none' && (
          <p className="self-end text-xs text-muted">If the server needs a reboot to finish its network settings, the install says so and you can reboot later.</p>
        )}
      </div>

      <p data-testid="prov-release" className="text-xs text-muted">
        Installs node release{' '}
        <span className="tabular font-mono text-text-soft">
          #{info?.release_no ?? '—'}
          {info?.image_tag ? ` · ${info.image_tag}` : ''}
        </span>
        . Takes 5–30 minutes; you can close this window, the install goes on.
      </p>

      <div data-testid="host-form-error">
        <Notice>{err}</Notice>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="quiet" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy} data-testid="host-install">
          {busy ? 'Starting…' : 'Install node'}
        </Button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Install progress
// ---------------------------------------------------------------------------

const STAGE_MARK: Record<InstallStage['status'], { mark: string; cls: string }> = {
  pending: { mark: '○', cls: 'text-faint' },
  running: { mark: '◉', cls: 'text-start' },
  done: { mark: '✓', cls: 'text-live' },
  failed: { mark: '✕', cls: 'text-fail' },
  skipped: { mark: '–', cls: 'text-muted' },
}

function jobStateText(j: InstallJob): { text: string; lamp: Lamp } {
  if (j.state === 'done') return { text: 'Installed', lamp: 'live' }
  if (j.state === 'failed') return { text: `Failed at ${STAGE_INFO[j.stage]?.title ?? j.stage}`, lamp: 'error' }
  return { text: `Running: ${STAGE_INFO[j.stage]?.title ?? j.stage}`, lamp: 'busy' }
}

/** One install job, live: GET /api/admin/nodes/provisioning/{id} plus host.job events. */
export function InstallProgress({ jobId, initial }: { jobId: string; initial?: InstallJob }) {
  const qc = useQueryClient()
  const factor = useSecondFactor()
  const now = useNow(1000)
  const poll = usePollWhileDown(5000)
  const q = useQuery({
    queryKey: jobKey(jobId),
    queryFn: () => hostsApi.job(jobId),
    initialData: initial,
    // host.job events carry every stage change; polling only while the stream is down.
    refetchInterval: (query) => (query.state.data?.state === 'running' ? poll : false),
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const j = q.data
  // Routing network (R0): the relay_key stage comes from the node's relay state after join.
  const joined = j?.stages.some((s) => s.name === 'join' && s.status === 'done')
  const nodesQ = useQuery({ queryKey: NODES_KEY, queryFn: () => get<NodeInfo[]>('/api/admin/nodes'), enabled: !!joined, refetchInterval: 5000 })
  if (!j) return q.error ? <Notice>{hostErrorText(q.error)}</Notice> : <p className="text-sm text-muted">Loading…</p>
  const p = j.params ?? {}
  const node = nodesQ.data?.find((n) => n.name === p.slug && n.status !== 'revoked')
  const { stages, derived } = stagesWithRelayKey(j, node)
  const relayKey = stages.find((s) => s.name === 'relay_key')
  const peersPending = j.state === 'done' && !!relayKey && relayKey.status !== 'done' && relayKey.status !== 'skipped'
  const st = peersPending ? { text: `Installed, waiting: ${STAGE_INFO.relay_key.title}`, lamp: 'busy' as Lamp } : jobStateText(j)
  const running = stages.find((s) => s.status === 'running')
  const wait = running && STAGE_WAIT[running.name]

  const retry = async () => {
    setBusy(true)
    setErr('')
    try {
      const again = await factor((totp) => hostsApi.retry(j.id, totp))
      qc.setQueryData(jobKey(j.id), again)
      void qc.invalidateQueries({ queryKey: HOSTS_KEY })
      void qc.invalidateQueries({ queryKey: hostJobsKey(j.host_id) })
    } catch (e) {
      if (!cancelled(e)) setErr(hostErrorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3" data-testid="install-progress" data-state={j.state} data-job={j.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium" data-testid="install-state">
          <Tally state={st.lamp} label={st.text} /> {st.text}
        </p>
        <p className="text-xs text-muted">
          {p.slug && <span className="font-mono text-text-soft">{p.slug}</span>}
          {p.address && <span className="font-mono"> · {p.address}</span>}
          {j.release_no !== undefined && <span className="num"> · release #{j.release_no}</span>}
          {j.created_at && <> · started {ago(j.created_at)}</>}
        </p>
      </div>

      {wait && (
        <p data-testid="install-wait" className="rounded-md border border-start/40 bg-start/[.07] px-3 py-2 text-xs text-text-soft">
          {wait}
        </p>
      )}

      <ol data-testid="install-stages" className="divide-y divide-line rounded-md border border-line">
        {stages.map((s) => {
          const m = STAGE_MARK[s.status]
          const d = stageDuration(s, now)
          const info = STAGE_INFO[s.name]
          return (
            <li
              key={s.name}
              data-testid={`stage-${s.name}`}
              data-status={s.status}
              data-derived={derived && s.name === 'relay_key' ? 'true' : undefined}
              className={`px-3 py-2 ${s.status === 'failed' ? 'bg-fail/[.06]' : ''}`}
            >
              <div className="flex items-start gap-3">
                <span aria-hidden className={`w-4 flex-none pt-0.5 text-center ${m.cls} ${s.status === 'running' ? 'animate-pulse' : ''}`}>
                  {m.mark}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span className={s.status === 'pending' ? 'text-muted' : 'font-medium'} title={info?.about}>
                      {info?.title ?? s.name}
                    </span>
                    <span className={`text-xs ${m.cls}`} data-testid={`stage-status-${s.name}`}>
                      {STAGE_STATUS_TEXT[s.status]}
                    </span>
                  </p>
                  {s.text && !(s.name === 'host_key' && s.tofu) && (
                    <p data-testid={`stage-text-${s.name}`} className={`mt-0.5 text-xs break-words ${s.status === 'failed' ? 'text-fail' : 'text-muted'}`}>
                      {s.text}
                    </p>
                  )}
                </div>
                <span data-testid={`stage-time-${s.name}`} className="tabular flex-none font-mono text-xs text-muted">
                  {d === undefined ? '' : clock(d)}
                </span>
              </div>
              {s.name === 'host_key' && s.tofu && (
                <div data-testid="stage-tofu" className="mt-2 ml-7 rounded-md border border-warn/60 bg-warn/[.08] px-3 py-2 text-xs">
                  <p className="font-medium text-warn">Host key accepted on first connection</p>
                  <p className="mt-1 font-mono text-[12px] break-all text-text">{s.text}</p>
                  <p className="mt-1 text-text-soft">Compare it with the fingerprint in the hosting console. If it differs, delete this host: someone else answered at this address.</p>
                </div>
              )}
              {s.status === 'failed' && s.output && (
                <div className="mt-2 ml-7">
                  <OutputBlock title="Server output (last 4 KB)" text={s.output} testId={`stage-output-${s.name}`} />
                </div>
              )}
            </li>
          )
        })}
      </ol>

      {j.state === 'failed' && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" disabled={busy} data-testid="install-retry" onClick={() => void retry()}>
            {busy ? 'Starting…' : 'Retry from this stage'}
          </Button>
          <span className="text-xs text-muted">Stages already done are kept; the failed one runs again.</span>
        </div>
      )}
      {j.state === 'done' && (
        <p data-testid="install-done" data-peers={peersPending ? 'pending' : undefined} className={`text-sm ${peersPending ? 'text-text-soft' : 'text-live'}`}>
          {p.slug ?? 'The node'} is installed and online. It shows up in the list of nodes.
          {peersPending && ' It stays out of relay routing until every node has acknowledged its relay key.'}
          {p.keep_ssh_access === false && ' The center’s SSH key was removed.'}
        </p>
      )}
      <div data-testid="install-error">
        <Notice>{err}</Notice>
      </div>
      {j.state === 'running' && <p className="text-xs text-muted">You can close this window: the install goes on, and its progress stays under “Hosts without a node” on this page.</p>}
    </div>
  )
}

/** Install jobs of a host, newest first (GET /api/admin/hosts/{id}/jobs; finished ones kept 7 days). */
export function HostJobs({ hostId }: { hostId: string }) {
  const q = useQuery({ queryKey: hostJobsKey(hostId), queryFn: () => hostsApi.jobs(hostId), refetchInterval: usePollWhileDown(30_000) })
  const [open, setOpen] = useState<string | null>(null)
  const jobs = q.data ?? []
  return (
    <section data-testid="host-jobs" className="space-y-2">
      <h4 className="text-sm font-medium">Install history</h4>
      {q.error && <Notice>{hostErrorText(q.error)}</Notice>}
      {q.data && !jobs.length && <p className="text-xs text-muted">No installs in the last 7 days.</p>}
      <ul className="space-y-2">
        {jobs.map((j) => {
          const st = jobStateText(j)
          return (
            <li key={j.id} data-testid={`host-job-${j.id}`} data-state={j.state} className="rounded-md border border-line">
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs">
                <span className="flex items-center gap-2">
                  <Tally state={st.lamp} label={st.text} /> {st.text}
                </span>
                <span className="text-muted">
                  {j.release_no !== undefined && <span className="num">release #{j.release_no} · </span>}
                  {ago(j.created_at)}
                </span>
                <Button variant="quiet" aria-expanded={open === j.id} data-testid={`host-job-open-${j.id}`} onClick={() => setOpen(open === j.id ? null : j.id)}>
                  {open === j.id ? 'Hide' : 'Stages'}
                </Button>
              </div>
              {open === j.id && (
                <div className="border-t border-line p-3">
                  <InstallProgress jobId={j.id} initial={j} />
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** The newest job of a host, for "Progress" on a host that is installing or failed. */
export function LatestJob({ hostId }: { hostId: string }) {
  const q = useQuery({ queryKey: hostJobsKey(hostId), queryFn: () => hostsApi.jobs(hostId) })
  if (q.error) return <Notice>{hostErrorText(q.error)}</Notice>
  if (!q.data) return <p className="text-sm text-muted">Loading…</p>
  const last = q.data[0]
  if (!last) return <p className="text-sm text-muted">No installs in the last 7 days.</p>
  return <InstallProgress jobId={last.id} initial={last} />
}

// ---------------------------------------------------------------------------
// The host of a node
// ---------------------------------------------------------------------------

/** "Agent: online" / "offline (since …)" / "installing" / "deleted". */
export function agentState(h: Host): { text: string; lamp: Lamp; key: 'online' | 'offline' | 'installing' | 'deleted' } {
  if (h.revoked_at) return { text: 'Deleted', lamp: 'error', key: 'deleted' }
  if (h.installing) return { text: 'Installing', lamp: 'busy', key: 'installing' }
  if (h.online) return { text: 'Agent online', lamp: 'live', key: 'online' }
  return { text: `Agent offline${h.last_seen ? ` (since ${ago(h.last_seen)})` : ''}`, lamp: 'dark', key: 'offline' }
}

function Fact({ label, children, testId }: { label: string; children: ReactNode; testId?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd data-testid={testId} className="text-sm break-words">
        {children}
      </dd>
    </div>
  )
}

const bytesMb = (mb: unknown) => (typeof mb === 'number' ? (mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`) : '—')
const uptime = (s: unknown) => {
  if (typeof s !== 'number') return '—'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  return d > 0 ? `${d} d ${h} h` : `${h} h ${Math.floor((s % 3600) / 60)} min`
}
const str = (v: unknown) => (v === undefined || v === null || v === '' ? undefined : String(v))
/** "14:05 UTC" today, else "2026-10-12 14:05 UTC" (console time, CN-06). */
const timeOf = (iso: string) => stampShort(iso)
const minutesUntil = (iso: string, now: number) => Math.max(0, Math.ceil((Date.parse(iso) - now) / 60_000))

// ---------------------------------------------------------------------------
// Reboots and the network facts of a host
// ---------------------------------------------------------------------------

/** A scheduled reboot, why one is needed, and what the center found after the last one. */
function RebootPanel({ h, st, onCancel, onReboot, busy }: { h: Host; st?: HostStatus; onCancel?: () => void; onReboot?: () => void; busy: boolean }) {
  const now = useNow(15_000)
  const w: RebootWatch | undefined = h.reboot
  const at = w?.state === 'scheduled' ? w.scheduled_at : st?.reboot_scheduled_at && Date.parse(st.reboot_scheduled_at) > now ? st.reboot_scheduled_at : undefined
  const reasons = st?.reboot_required ? (st.reboot_reasons ?? []) : []
  const shown = w && w.state !== 'scheduled' && w.finished_at ? now - Date.parse(w.finished_at) < 7 * 86400_000 : w?.state === 'checking'
  if (!at && !reasons.length && !shown) return null
  return (
    <section data-testid="host-reboot" className="space-y-2">
      {at && (
        <div data-testid="host-reboot-scheduled" className="flex flex-wrap items-center gap-3 rounded-md border border-fail/50 bg-fail/[.07] px-3 py-2 text-sm">
          <span className="font-medium text-fail">
            Reboot at <span data-testid="host-reboot-at">{timeOf(at)}</span>
          </span>
          <span className="text-xs text-text-soft">in {minutesUntil(at, now)} min · broadcasts on the node drop then</span>
          {onCancel && (
            <Button className="ml-auto" disabled={busy} data-testid="host-cmd-reboot_cancel" onClick={onCancel}>
              {busy ? '…' : 'Cancel reboot'}
            </Button>
          )}
        </div>
      )}
      {!at && reasons.length > 0 && (
        <div data-testid="host-reboot-required" className="flex flex-wrap items-start gap-3 rounded-md border border-warn/60 bg-warn/[.08] px-3 py-2 text-xs">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-warn">▲ The server needs a reboot</p>
            <ul className="mt-1 list-disc pl-4 text-text-soft">
              {reasons.map((r) => (
                <li key={r} data-testid="host-reboot-reason" className="break-words">
                  {r}
                </li>
              ))}
            </ul>
          </div>
          {onReboot && (
            <Button data-testid="host-reboot-required-reboot" onClick={onReboot}>
              Reboot…
            </Button>
          )}
        </div>
      )}
      {w && shown && (
        <div
          data-testid="host-reboot-check"
          data-state={w.state}
          className={`rounded-md border px-3 py-2 text-xs ${w.state === 'failed' ? 'border-fail/60 bg-fail/[.07]' : w.state === 'ok' ? 'border-live/40 bg-live/[.05]' : 'border-line'}`}
        >
          <p className={`font-medium ${w.state === 'failed' ? 'text-fail' : w.state === 'ok' ? 'text-live' : 'text-text-soft'}`}>
            {REBOOT_STATE_TEXT[w.state]}
            <span className="ml-2 font-normal text-muted">reboot of {timeOf(w.scheduled_at)}</span>
          </p>
          {w.error && <p className="mt-1 break-words text-fail">{w.error}</p>}
          {!!w.checks?.length && (
            <ul className="mt-1.5 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
              {w.checks.map((c) => (
                <li key={c.name} data-testid={`host-reboot-check-${c.name}`} data-ok={c.ok} className={c.ok ? 'text-text-soft' : 'text-fail'}>
                  {c.ok ? '✓' : '✕'} {REBOOT_CHECK_TEXT[c.name] ?? c.name}
                  {c.text && <span className="text-muted"> — {c.text}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

const num = (v?: number) => (v === undefined ? '—' : v.toLocaleString())

/** The network facts of the last status, deviations highlighted (docs/edge-node.md, «Сеть хоста»). */
function NetFacts({ n, at, onRefresh, busy }: { n?: HostNetwork; at?: string; onRefresh?: () => void; busy: boolean }) {
  const warn = new Set<NetWarning>(netWarnings(n))
  const row = (key: string, label: string, value: ReactNode, w?: NetWarning | string) => {
    // A NetWarning when the agent reported it, or a note of the console's own.
    const on = !!w && (warn.has(w as NetWarning) || !(w in NET_WARNING_TEXT))
    return (
      <tr key={key} data-testid={`net-${key}`} data-warn={on ? 'true' : undefined} className={on ? 'bg-warn/[.08]' : ''}>
        <th scope="row" className="px-3 py-1.5 text-left text-xs font-normal text-muted">
          {label}
        </th>
        <td className={`px-3 py-1.5 font-mono text-xs ${on ? 'text-warn' : 'text-text-soft'}`}>
          {value}
          {on && <span className="ml-2 font-sans text-[11px]">▲ {NET_WARNING_TEXT[w as NetWarning] ?? w}</span>}
        </td>
      </tr>
    )
  }
  const sysctlOff = n && n.node_slow_start_after_idle !== undefined && (n.node_slow_start_after_idle !== 0 || n.node_mtu_probing !== 1)
  return (
    <section data-testid="host-net" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium">
          Network{' '}
          {warn.size > 0 && (
            <span data-testid="host-net-warnings" className="ml-1 text-xs font-normal text-warn">
              ▲ {warn.size} to fix
            </span>
          )}
        </h4>
        <span className="flex items-center gap-2 text-xs text-muted">
          {at ? `from the status of ${ago(at)}` : 'no status yet'}
          {onRefresh && (
            <Button variant="quiet" disabled={busy} data-testid="host-net-refresh" onClick={onRefresh}>
              {busy ? '…' : 'Refresh'}
            </Button>
          )}
        </span>
      </div>
      {!n ? (
        <p className="text-xs text-muted">Run Status to see the NIC, qdisc, queues, TCP algorithm and time sync of the server.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal w-full min-w-[420px]">
            <tbody>
              {row('cc', 'TCP algorithm (host / node)', `${n.host_cc || '—'} / ${n.node_cc || '—'}`, 'not_bbr')}
              {row('qdisc', `qdisc${n.dev ? ` on ${n.dev}` : ''}`, `${n.qdisc || '—'}${n.fq ? ' · fq on every queue' : ''}`, 'no_fq')}
              {row('queues', 'NIC queues (in use / allowed · CPUs)', `${num(n.queues)} / ${n.queues_max ? num(n.queues_max) : '—'} · ${num(n.cpus)}`, 'queues')}
              {row('time', 'Time sync', n.time_synced === undefined ? '—' : n.time_synced ? 'synchronized' : 'not synchronized', 'time_not_synced')}
              {row('sysctl', 'Node: slow start after idle / MTU probing', `${n.node_slow_start_after_idle ?? '—'} / ${n.node_mtu_probing ?? '—'}`, sysctlOff ? 'Expected 0 / 1 (compose.yaml of this release)' : undefined)}
              {row('softnet', 'softnet dropped / time squeeze (since boot)', `${num(n.softnet_dropped)} / ${num(n.softnet_time_squeeze)}`)}
              {row('conntrack', 'conntrack invalid (since boot)', n.conntrack_invalid === undefined ? 'no conntrack tool' : num(n.conntrack_invalid))}
              {row('proxy', 'External connections via docker-proxy', num(n.docker_proxy_external), 'docker_proxy')}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/** What a command answered, by kind (HostCommand.data in the contract). */
function CommandData({ c, onReboot }: { c: HostCommand; onReboot?: () => void }) {
  const d = c.data
  if (!d) return null
  const out = str(d.output)
  switch (c.kind) {
    case 'status':
      return (
        <p data-testid={`cmd-status-${c.seq}`} className="text-xs text-text-soft">
          {[
            d.release_no !== undefined && `release #${d.release_no}`,
            str(d.agent_version) && `agent ${d.agent_version}`,
            str(d.os),
            str(d.kernel) && `kernel ${d.kernel}`,
            `up ${uptime(d.uptime_s)}`,
            `disk free ${bytesMb(d.disk_free_mb)}`,
            typeof d.load1 === 'number' && `load ${d.load1.toFixed(2)}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )
    case 'diagnostics': {
      const sections = (d.sections ?? {}) as Record<string, unknown>
      return (
        <div className="space-y-1.5">
          {Object.entries(sections).map(([k, v]) => (
            <OutputBlock key={k} title={k} text={String(v ?? '')} testId={`cmd-diag-${c.seq}-${k}`} />
          ))}
        </div>
      )
    }
    case 'os_update':
      return (
        <div className="space-y-1.5">
          {d.reboot_required === true && (
            <div data-testid={`cmd-reboot-required-${c.seq}`} className="flex flex-wrap items-center gap-3 rounded-md border border-warn/60 bg-warn/[.08] px-3 py-2 text-xs">
              <span className="font-medium text-warn">▲ Reboot needed to finish the updates</span>
              {onReboot && (
                <Button data-testid={`cmd-reboot-now-${c.seq}`} onClick={onReboot}>
                  Reboot…
                </Button>
              )}
            </div>
          )}
          {out && <OutputBlock title="Output" text={out} />}
        </div>
      )
    case 'deploy':
    case 'redeploy':
    case 'rollback':
      return (
        <div className="space-y-1.5">
          <p data-testid={`cmd-release-${c.seq}`} className="text-xs text-text-soft">
            Release <span className="tabular font-mono">#{String(d.release_no ?? '—')}</span>
            {str(d.image_tag) && <span className="font-mono"> · {String(d.image_tag)}</span>}
            {d.agent_update === true && ' · agent updated'}
          </p>
          {d.rolled_back === true && (
            <p data-testid={`cmd-rolled-back-${c.seq}`} className="rounded-md border border-warn/60 bg-warn/[.08] px-3 py-2 text-xs text-warn">
              ▲ The new release did not come up healthy: the node is back on the previous one.
            </p>
          )}
          {d.reboot_required === true && (
            <div data-testid={`cmd-reboot-required-${c.seq}`} className="flex flex-wrap items-center gap-3 rounded-md border border-warn/60 bg-warn/[.08] px-3 py-2 text-xs">
              <span className="font-medium text-warn">▲ Reboot needed to finish the network settings</span>
              {Array.isArray(d.reboot_reasons) && <span className="text-text-soft">{(d.reboot_reasons as string[]).join('; ')}</span>}
              {onReboot && (
                <Button data-testid={`cmd-reboot-now-${c.seq}`} onClick={onReboot}>
                  Reboot…
                </Button>
              )}
            </div>
          )}
          {out && <OutputBlock title="Output" text={out} />}
        </div>
      )
    case 'restart_node':
    case 'update': {
      // HOLD-A8: the agent's switch as it came — the outcome in words, then the steps.
      const svc = serviceOf(c)!
      if (c.state === 'sent' || c.state === 'timeout') return out ? <OutputBlock title="Output" text={out} /> : null
      const o = outcome(svc, c, { name: '', release: typeof d.release_no === 'number' ? d.release_no : undefined, streams: typeof d.streams === 'number' ? d.streams : 0 })
      return <SwitchOutcome o={o} steps={Array.isArray(d.steps) ? (d.steps as Parameters<typeof SwitchOutcome>[0]['steps']) : undefined} testId={`cmd-switch-${c.seq}`} />
    }
    case 'reboot':
      return (
        <p data-testid={`cmd-reboot-${c.seq}`} className="text-xs text-text-soft">
          Rebooting at {d.at ? timeOf(String(d.at)) : '—'}. Broadcasts on the node drop until it is back.
          {c.live && (c.live.streams > 0 || c.live.outputs > 0) && <span className="text-warn"> Ordered with {liveText(c.live)} on the node.</span>}
        </p>
      )
    case 'reboot_cancel':
      return (
        <p className="text-xs text-text-soft">
          {d.was_scheduled_at ? `The reboot due at ${timeOf(String(d.was_scheduled_at))} is cancelled.` : 'No reboot was scheduled.'}
        </p>
      )
    case 'remove_ssh_access':
      return <p className="text-xs text-text-soft">{d.removed ? "The center's key was removed from authorized_keys." : "The center's key was not in authorized_keys."}</p>
    case 'rebind':
      return (
        <p className="text-xs text-text-soft">
          The node joined again as <span className="font-mono">{String(d.node_id ?? '—')}</span>; the old record is revoked.
        </p>
      )
    case 'relay_enable':
      return (
        <p data-testid={`cmd-relay-${c.seq}`} className="text-xs text-text-soft">
          {d.spki ? (
            <>
              RELAY=true on the host; the node runs relay key <span className="font-mono">{String(d.spki).slice(0, 8)}</span>, registered by the center from this result.
            </>
          ) : (
            'No relay key in the result: the host agent is older than this console (update the host).'
          )}
        </p>
      )
    case 'relay_disable':
      return <p className="text-xs text-text-soft">RELAY=false on the host; the media node restarted without the relay. Its key stays registered.</p>
    default:
      return out ? <OutputBlock title="Output" text={out} /> : null
  }
}

const STATE_CLS: Record<HostCommand['state'], string> = {
  sent: 'border-start/50 text-start',
  ok: 'border-live/50 text-live',
  failed: 'border-fail/50 text-fail',
  timeout: 'border-warn/50 text-warn',
}

function CommandRow({ c, me, onReboot }: { c: HostCommand; me?: number; onReboot?: () => void }) {
  const who = c.admin_id === undefined ? '—' : c.admin_id === me ? 'you' : `admin #${c.admin_id}`
  const args = c.args && Object.keys(c.args).length ? Object.entries(c.args).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`).join(', ') : ''
  return (
    <li data-testid={`host-cmd-row-${c.seq}`} data-kind={c.kind} data-state={c.state} className="space-y-1.5 px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-medium">{COMMAND_LABEL[c.kind] ?? c.kind}</span>
        {args && <span className="font-mono text-xs text-muted">{args}</span>}
        <span data-testid={`host-cmd-state-${c.seq}`} className={`rounded-sm border px-1.5 text-xs ${STATE_CLS[c.state]}`}>
          {COMMAND_STATE_TEXT[c.state]}
        </span>
        <span className="ml-auto text-xs text-muted">
          {who} · {ago(c.issued_at)}
        </span>
      </div>
      {c.error && <p className="text-xs break-words text-fail">{c.error}</p>}
      <CommandData c={c} onReboot={onReboot} />
    </li>
  )
}

/** A command waiting for a look first; word: what to type (from confirm_required once the server named it). */
type Pending = { kind: HostCommandKind; args?: Record<string, unknown>; word?: string; reboot?: RebootPlan; live?: LiveCount; requestId: string } | null

/** One id per confirmation shown (or per click without one): the same id sent again — a double submit — gets the first answer, not a second command (PROV-RELAY-1). */
export function newRequestId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
  }
}

const CONFIRM_VERB: Partial<Record<HostCommandKind, string>> = { reboot: 'reboot', rebind: 'rebind', unenroll: 'delete' }

const streamsText = (n?: NodeInfo) =>
  n?.streams ? `${n.streams} live stream${n.streams === 1 ? '' : 's'} on this node will drop.` : 'Broadcasts on this node will drop.'

/** When a reboot happens, as the dialog says it. */
const rebootWhen = (args?: Record<string, unknown>, plan?: RebootPlan) => {
  const d = Number(args?.delay_minutes ?? (typeof plan === 'object' ? plan.delay_minutes : 0))
  return d > 1 ? `in ${d} minutes` : 'in a minute'
}

const DANGER_TEXT: Partial<Record<HostCommandKind, (h: Host, node?: NodeInfo, p?: Pending) => string>> = {
  reboot: (_h, n, p) => `${streamsText(n)} The server restarts ${rebootWhen(p?.args)} and the node comes back by itself; the center then checks it. You can cancel until then.`,
  deploy: (_h, n, p) => `The node updates to the center's release and, once it is healthy, the server restarts ${rebootWhen(undefined, p?.reboot)}. ${streamsText(n)}`,
  redeploy: (_h, n, p) => `The node's files and images are installed again and, once it is healthy, the server restarts ${rebootWhen(undefined, p?.reboot)}. ${streamsText(n)}`,
  rebind: (_h, n) =>
    `${streamsText(n)} The media node stops, forgets its identity and joins again with a new id; the old node record is revoked. Its settings (roles, egress, ingest, relay, transit, congestion control, provider) carry over to the new id. Use it when the node can't reach the center any more; to turn the relay on, use Enable relay instead.`,
  relay_disable: (_h, n) =>
    `The node leaves the routing network: every node drops it from its peers list${n?.relay?.legs ? ` and ${n.relay.legs} live leg${n.relay.legs === 1 ? '' : 's'} through it end` : ''}. RELAY=false on the host and the media node restarts (broadcasts on it reconnect). Its relay key stays registered: Enable relay brings it back with the same key.`,
  unenroll: (h) =>
    `The media node stops, the agent removes its key, and the node is revoked here.${h.dns_managed ? ` The DNS record ${h.hostname ?? ''} is removed.` : ''} There is no undo: a new install is needed to use the server again.`,
  remove_ssh_access: () => "The center's key is removed from authorized_keys. The agent stays the only channel: if it breaks, the center can't repair the server.",
  rollback: () => 'The node goes back to an earlier release kept on the server (the previous one unless you name another).',
}

/** What the console says after relay_enable / relay_disable, from the center's part of the answer. */
export function relayResultText(kind: HostCommandKind, r: NonNullable<HostCommand['relay']>): { kind: 'error' | 'info'; text: string } {
  if (r.error) {
    switch (r.code) {
      case 'spki_in_use':
        return { kind: 'error', text: 'The relay is on on the host, but another node already has this relay key: the center did not register it. Rebind this node to give it a new key.' }
      case 'key_differs':
        return { kind: 'error', text: 'The center has another relay key for this node than the one on its host. Rebind the node (a new identity with a new key; its settings carry over).' }
      case 'bad_result':
        return { kind: 'error', text: 'The host agent did not report the relay key: it is older than this console. Update the host, then Enable relay again.' }
    }
    return { kind: 'error', text: `${COMMAND_LABEL[kind]}: ${r.error}` }
  }
  if (kind === 'relay_disable') return { kind: 'info', text: 'Relay off: the node left the peers list. Its key stays registered.' }
  return {
    kind: 'info',
    text: `Relay on for ${r.node_id}${r.registered ? `: key ${r.spki_prefix ?? ''} registered` : ''}. Every node gets the new peers list now; the node card shows who trusts it.`,
  }
}

/**
 * A host's card: agent, release, SSH access, address and DNS, actions and the
 * last commands (GET /api/admin/hosts/{id} with commands, host.command events).
 */
export function HostCard({ hostId, info, node, badge, onGone }: { hostId: string; info?: InstallInfo; node?: NodeInfo; badge?: boolean; onGone?: () => void }) {
  const qc = useQueryClient()
  const factor = useSecondFactor()
  const { user } = useSession()
  const q = useQuery({ queryKey: hostKey(hostId), queryFn: () => hostsApi.host(hostId), refetchInterval: usePollWhileDown(15_000) })
  const [pending, setPending] = useState<Pending>(null)
  const [word, setWord] = useState('')
  const [busy, setBusy] = useState<HostCommandKind | null>(null)
  const [msg, setMsg] = useState<{ kind: 'error' | 'info'; text: string } | null>(null)
  const [service, setService] = useState<'medianode' | 'caddy' | 'all'>('medianode')
  const [packages, setPackages] = useState('')
  const [showJobs, setShowJobs] = useState(false)
  const [rollbackTo, setRollbackTo] = useState('')
  const [delay, setDelay] = useState(String(REBOOT_DELAYS[2]))
  const [afterUpdate, setAfterUpdate] = useState<RebootChoice>('none')
  const [liveOk, setLiveOk] = useState(false)
  const [restartOpen, setRestartOpen] = useState(false)
  const restartRef = useRef<HTMLButtonElement>(null)
  const [restartToast, setRestartToast] = useState<ReturnType<typeof switchDone> | null>(null)
  useEffect(() => {
    setWord('')
    setLiveOk(false)
  }, [pending?.kind, pending?.args, pending?.reboot])

  const h = q.data
  const pkgs = useMemo(() => packages.split(/[\s,]+/).filter(Boolean), [packages])
  const pkgError = !pkgs.length ? '' : pkgs.length > MAX_PACKAGES ? `At most ${MAX_PACKAGES} packages` : pkgs.find((p) => !PACKAGE_RE.test(p)) ? `Not a package name: ${pkgs.find((p) => !PACKAGE_RE.test(p))}` : ''

  if (!h) return q.error ? <Notice>{hostErrorText(q.error)}</Notice> : <p className="text-sm text-muted">Loading the host…</p>

  const a = agentState(h)
  const behind = releasesBehind(h, info)
  const deleted = !!h.revoked_at
  const offline = !h.online
  const commands = h.commands ?? []

  const send = async (kind: HostCommandKind, args?: Record<string, unknown>, confirm?: string, extra: { reboot?: RebootPlan; confirm_live?: boolean; requestId?: string } = {}) => {
    const requestId = extra.requestId ?? newRequestId()
    setBusy(kind)
    setMsg(null)
    try {
      const c = await factor((totp) =>
        hostsApi.command(h.id, {
          kind,
          ...(args ? { args } : {}),
          ...(confirm ? { confirm } : {}),
          ...(extra.reboot && extra.reboot !== 'none' ? { reboot: extra.reboot } : {}),
          ...(extra.confirm_live ? { confirm_live: true } : {}),
          request_id: requestId,
          ...(totp && !NO_FACTOR.includes(kind) ? { totp } : {}),
        }),
      )
      qc.setQueryData(hostKey(h.id), (x: typeof h | undefined) => (x ? withCommand(x, c) : x))
      void qc.invalidateQueries({ queryKey: hostKey(h.id) })
      void qc.invalidateQueries({ queryKey: HOSTS_KEY })
      void qc.invalidateQueries({ queryKey: TOTP_KEY })
      if (kind === 'rebind' || kind === 'unenroll' || kind === 'relay_enable' || kind === 'relay_disable') void qc.invalidateQueries({ queryKey: ['admin-nodes'] })
      setPending(null)
      if (c.relay && c.state === 'ok') {
        setMsg(relayResultText(kind, c.relay))
        return
      }
      if (c.reboot) qc.setQueryData(hostKey(h.id), (x: typeof h | undefined) => (x && c.reboot ? withCommand(x, c.reboot) : x))
      if (c.state === 'timeout') setMsg({ kind: 'info', text: `${COMMAND_LABEL[kind]}: sent, waiting for the agent's answer. It shows up below.` })
      else if (c.state === 'failed') setMsg({ kind: 'error', text: `${COMMAND_LABEL[kind]} failed${c.error ? `: ${c.error}` : ''}.` })
      else if (c.reboot) setMsg({ kind: c.reboot.state === 'ok' ? 'info' : 'error', text: `${COMMAND_LABEL[kind]}: done. ${c.reboot.state === 'ok' ? `Reboot scheduled at ${timeOf(String(c.reboot.data?.at ?? ''))}.` : `The reboot was not scheduled: ${c.reboot.error ?? c.reboot.state}.`}` })
      else if (kind === 'reboot' && c.data?.at) setMsg({ kind: 'info', text: `Reboot scheduled at ${timeOf(String(c.data.at))}.` })
      else setMsg({ kind: 'info', text: `${COMMAND_LABEL[kind]}: done.` })
      if (kind === 'unenroll' && c.state === 'ok') onGone?.()
    } catch (e) {
      const h2 = hostError(e)
      if (h2?.code === 'confirm_required') {
        // The server names the word; the local table only filled the first look.
        setPending({ kind, args, word: h2.confirm ?? CONFIRM_WORD[kind], reboot: extra.reboot, requestId })
        setMsg({ kind: 'error', text: hostErrorText(e) })
      } else if (h2?.code === 'live_on_node') {
        // Live streams on the node: the same order again, with what drops.
        setPending({ kind, args, word: confirm ? undefined : CONFIRM_WORD[kind], reboot: extra.reboot, live: h2.live ?? { streams: 0, outputs: 0 }, requestId })
        if (confirm) setWord(confirm)
        setMsg(null)
      } else if (!cancelled(e)) setMsg({ kind: 'error', text: hostErrorText(e) })
    } finally {
      setBusy(null)
    }
  }

  /** Commands that need a look first (a typed word or a plain confirm). */
  const ask = (kind: HostCommandKind, args?: Record<string, unknown>, reboot?: RebootPlan) => {
    setMsg(null)
    setRollbackTo('')
    const word = CONFIRM_WORD[kind] ?? (reboot && reboot !== 'none' ? 'REBOOT' : undefined)
    setPending({ kind, args, word, reboot, requestId: newRequestId() })
  }
  /** Update and redeploy go straight out, or through the dialog when a reboot follows. */
  const update = (kind: 'deploy' | 'redeploy') => {
    const plan = rebootPlan(afterUpdate, delay)
    if (plan && plan !== 'none') ask(kind, undefined, plan)
    else void send(kind)
  }
  const st = h.status

  const disabledWhy = deleted ? 'The host is deleted' : h.installing ? 'An install is running' : offline ? 'The agent is offline' : ''
  // A split node with publishers: holders, Caddy, the OS and a reboot wait until the node is freed (HOLD-A8).
  const free = node ? freeFirst(h, publishersOn(node)) : ''
  const restartWhy = disabledWhy || switchBlock('restart', h, info)
  const btn = (kind: HostCommandKind, run: () => void, opts: { variant?: 'primary' | 'plain' | 'danger'; label?: string; always?: boolean; extra?: Record<string, string>; holders?: boolean } = {}) => {
    const why = opts.always ? (deleted ? 'The host is deleted' : '') : disabledWhy || (opts.holders ? free : '')
    const needs = NO_FACTOR.includes(kind) ? '' : CONFIRM_WORD[kind] ? ' (code and typed word)' : ' (needs your code)'
    return (
      <Button
        variant={opts.variant ?? 'plain'}
        disabled={!!why || !!busy}
        title={why || `${COMMAND_LABEL[kind]}${needs}`}
        data-testid={`host-cmd-${kind}`}
        onClick={run}
        {...opts.extra}
      >
        {busy === kind ? '…' : (opts.label ?? COMMAND_LABEL[kind])}
      </Button>
    )
  }

  const pendingWord = pending?.word
  const tone = pending && (pending.kind === 'unenroll' || pending.kind === 'rebind' || pending.kind === 'reboot' || !!pending.reboot) ? 'fail' : 'warn'

  return (
    <div data-testid="host-card" data-host={h.id} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-medium">
          Host <span className="font-mono text-sm text-text-soft">{h.slug}</span>
          <span data-testid="host-agent" data-agent={a.key} className="flex items-center gap-2 text-sm font-normal">
            <Tally state={a.lamp} label={a.text} /> {a.text}
          </span>
        </h3>
        {badge && <FactorBadge />}
      </div>

      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Release" testId="host-release">
          <span className="tabular font-mono">{h.release_no !== undefined ? `#${h.release_no}` : '—'}</span>
          {behind > 0 ? (
            <span data-testid="host-lag" className="ml-2 text-xs text-warn">
              ▲ {behind} release{behind === 1 ? '' : 's'} behind the center (#{info?.release_no})
            </span>
          ) : (
            info?.release_no !== undefined && h.release_no !== undefined && <span className="ml-2 text-xs text-muted">up to date</span>
          )}
        </Fact>
        <Fact label="Agent" testId="host-agent-version">
          <span className="font-mono text-xs">{h.agent_version || '—'}</span>
        </Fact>
        <Fact label="OS" testId="host-os">
          {h.os || '—'}
        </Fact>
        <Fact label="Center's SSH access" testId="host-ssh">
          {h.ssh_access ? (
            <span className="text-warn">Yes · backup channel</span>
          ) : (
            <span className="text-text-soft">No</span>
          )}
        </Fact>
        <Fact label="Address" testId="host-address">
          <Mono>{h.address || '—'}</Mono>
        </Fact>
        <Fact label="DNS name" testId="host-hostname">
          <Mono>{h.hostname || '—'}</Mono>
          <span className="block text-xs text-muted">{h.dns_managed ? 'record made by the center' : 'record managed by hand'}</span>
        </Fact>
        <Fact label="Host SSH key" testId="host-ssh-key">
          <Mono>{h.ssh_host_key || '—'}</Mono>
        </Fact>
        <Fact label="Last seen" testId="host-last-seen">
          {h.online ? 'now' : ago(h.last_seen)}
        </Fact>
      </dl>

      {!deleted && (
        <RebootPanel
          h={h}
          st={st}
          busy={busy === 'reboot_cancel'}
          onCancel={disabledWhy ? undefined : () => void send('reboot_cancel')}
          onReboot={disabledWhy || free ? undefined : () => ask('reboot')}
        />
      )}
      {!deleted && <NetFacts n={st?.network} at={h.status_at} busy={busy === 'status'} onRefresh={disabledWhy ? undefined : () => void send('status')} />}

      {!deleted && (
        <section data-testid="host-actions" className="space-y-3">
          {disabledWhy && (
            <p data-testid="host-actions-off" className="text-xs text-muted">
              {disabledWhy}: actions are off{offline && !h.installing ? ', except deleting the host' : ''}.
            </p>
          )}
          {free && (
            <p data-testid="host-free-first" className="text-xs text-muted">
              {free}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {behind > 0 ? (
              btn('deploy', () => update('deploy'), {
                variant: 'primary',
                label: `Update to #${info?.release_no}`,
                extra: { 'data-attention': 'true' },
                holders: true,
              })
            ) : (
              <SxButton
                ref={restartRef}
                className="max-md:!h-[44px]"
                disabled={!!restartWhy || !!busy}
                title={restartWhy || 'Restart node (needs your code)'}
                data-testid="host-cmd-restart_node"
                onClick={() => {
                  setMsg(null)
                  setRestartOpen(true)
                }}
              >
                Restart node
              </SxButton>
            )}
            {btn('redeploy', () => update('redeploy'), { holders: true })}
            {btn('rollback', () => ask('rollback'), { holders: true })}
            {btn('os_update', () => void send('os_update'), { holders: true })}
            {btn('status', () => void send('status'))}
            {btn('diagnostics', () => void send('diagnostics'))}
          </div>
          {behind === 0 && !disabledWhy && restartWhy && (
            <p data-testid="host-restart-why" className="text-xs text-muted">
              {restartWhy}
            </p>
          )}
          <div className="w-72" data-testid="host-after-update">
            <Segmented label="Reboot after Update / Redeploy" options={REBOOT_OPTS} value={afterUpdate} onChange={setAfterUpdate} />
            {afterUpdate === 'later' && <p className="mt-1 text-xs text-muted">{`In ${delay} min (the delay below)`}</p>}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-64">
              <Segmented
                label="Restart service"
                options={[
                  { value: 'medianode', label: 'Media node' },
                  { value: 'caddy', label: 'Caddy' },
                  { value: 'all', label: 'All' },
                ]}
                value={service}
                onChange={setService}
              />
            </div>
            {btn('restart', () => void send('restart', { service }), { holders: service !== 'medianode' })}
          </div>
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-0 flex-1 sm:max-w-md">
              <Field
                label="Packages"
                name={`host-packages-${h.id}`}
                data-testid="host-packages"
                placeholder="htop iotop"
                autoComplete="off"
                spellCheck={false}
                value={packages}
                error={pkgError}
                hint={`Only from the official Ubuntu repositories already set up on the server. Space-separated names, at most ${MAX_PACKAGES}.`}
                onChange={(e) => setPackages(e.target.value)}
              />
            </div>
            <div className="pt-6">{btn('install_packages', () => !pkgError && pkgs.length && void send('install_packages', { names: pkgs }))}</div>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-line pt-3">
            {h.ssh_access && btn('remove_ssh_access', () => ask('remove_ssh_access'))}
            {btn('reboot', () => ask('reboot'), { variant: 'danger', label: 'Reboot now…', holders: true })}
            <span className="flex items-center gap-1.5">
              <select
                aria-label="Reboot delay"
                data-testid="host-reboot-delay"
                value={delay}
                onChange={(e) => setDelay(e.target.value)}
                disabled={!!disabledWhy}
                className="rounded-md border border-line-2 bg-bg px-2 py-1.5 text-xs text-text"
              >
                {REBOOT_DELAYS.map((m) => (
                  <option key={m} value={String(m)}>
                    {m < 60 ? `${m} min` : `${m / 60} h`}
                  </option>
                ))}
              </select>
              {btn('reboot', () => ask('reboot', { delay_minutes: Number(delay) }), {
                variant: 'danger',
                label: `Reboot in ${Number(delay) < 60 ? `${delay} min` : `${Number(delay) / 60} h`}…`,
                extra: { 'data-testid': 'host-cmd-reboot-later' },
              })}
            </span>
            {node && !node.revoked_at && (node.relay?.enabled && node.relay.state !== 'unsupported' && node.relay.state !== 'unregistered'
              ? btn('relay_disable', () => ask('relay_disable'), { label: 'Disable relay…' })
              : btn('relay_enable', () => void send('relay_enable'), {
                  variant: node.relay && node.relay.state !== 'disabled' ? 'primary' : 'plain',
                  extra: node.relay && node.relay.state !== 'disabled' ? { 'data-attention': 'true' } : {},
                }))}
            {btn('rebind', () => ask('rebind'), { variant: 'danger', label: 'Rebind…' })}
            {btn('unenroll', () => ask('unenroll'), { variant: 'danger', label: 'Delete host…', always: true })}
          </div>

          {pending && (
            <div
              role="alertdialog"
              aria-label={`${COMMAND_LABEL[pending.kind]} ${h.slug}?`}
              data-testid={`host-confirm-${pending.kind}`}
              className={`space-y-2.5 rounded-md border p-3 ${tone === 'fail' ? 'border-fail/50 bg-fail/[.07]' : 'border-warn/50 bg-warn/[.07]'}`}
            >
              <p className={`font-medium ${tone === 'fail' ? 'text-fail' : 'text-warn'}`}>
                {COMMAND_LABEL[pending.kind]} {h.slug}?
              </p>
              <p data-testid="host-confirm-text" className="text-[13px] text-text-soft">
                {DANGER_TEXT[pending.kind]?.(h, node, pending)}
              </p>
              {pending.live && (
                <label data-testid="host-confirm-live" className="flex items-start gap-2 rounded-md border border-fail/50 bg-bg/60 px-3 py-2 text-[13px]">
                  <input
                    type="checkbox"
                    data-testid="host-confirm-live-check"
                    checked={liveOk}
                    onChange={(e) => setLiveOk(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[var(--fail)]"
                  />
                  <span>
                    <span className="font-medium text-fail">{liveText(pending.live)} run on this node now.</span>
                    <span className="block text-text-soft">They drop with the reboot. Reboot anyway.</span>
                  </span>
                </label>
              )}
              {pending.kind === 'rollback' && (
                <div className="max-w-xs">
                  <Field
                    label="Release to go back to"
                    name={`host-rollback-${h.id}`}
                    data-testid="host-rollback-release"
                    inputMode="numeric"
                    placeholder="previous"
                    autoComplete="off"
                    value={rollbackTo}
                    error={rollbackTo && !/^[1-9]\d{0,9}$/.test(rollbackTo) ? 'A release number, or empty for the previous one' : undefined}
                    hint="Empty: the previous release"
                    onChange={(e) => setRollbackTo(e.target.value.trim())}
                  />
                </div>
              )}
              {pendingWord && (
                <Field
                  label={`Type ${pendingWord} to ${CONFIRM_VERB[pending.kind] ?? 'confirm'} ${h.slug}`}
                  name={`host-confirm-word-${h.id}`}
                  data-testid="host-confirm-input"
                  autoComplete="off"
                  spellCheck={false}
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                />
              )}
              <div className="flex gap-2">
                <Button variant="quiet" data-testid="host-confirm-cancel" onClick={() => setPending(null)}>
                  Cancel
                </Button>
                <Button
                  variant={tone === 'fail' ? 'danger' : 'primary'}
                  disabled={
                    !!busy ||
                    (!!pendingWord && word !== pendingWord) ||
                    (!!pending.live && !liveOk) ||
                    (pending.kind === 'rollback' && !!rollbackTo && !/^[1-9]\d{0,9}$/.test(rollbackTo))
                  }
                  data-testid="host-confirm-apply"
                  onClick={() =>
                    void send(
                      pending.kind,
                      pending.kind === 'rollback' && rollbackTo ? { release_no: Number(rollbackTo) } : pending.args,
                      pendingWord ? word : pending.live && word ? word : undefined,
                      { reboot: pending.reboot, confirm_live: !!pending.live && liveOk, requestId: pending.requestId },
                    )
                  }
                >
                  {busy ? '…' : COMMAND_LABEL[pending.kind]}
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
      {msg && (
        <div data-testid="host-msg" data-kind={msg.kind}>
          <Notice kind={msg.kind}>{msg.text}</Notice>
        </div>
      )}
      {restartOpen && (
        <RestartNodeDialog
          hostId={h.id}
          name={node?.name || h.slug}
          releaseNo={h.release_no}
          streams={node?.streams ?? 0}
          returnFocus={restartRef}
          onClose={() => setRestartOpen(false)}
          onDone={(c) => {
            setRestartOpen(false)
            qc.setQueryData(hostKey(h.id), (x: typeof h | undefined) => (x ? withCommand(x, c) : x))
            void qc.invalidateQueries({ queryKey: hostKey(h.id) })
            void qc.invalidateQueries({ queryKey: HOSTS_KEY })
            void qc.invalidateQueries({ queryKey: TOTP_KEY })
            setRestartToast(switchDone('restart', c, { nodeId: node?.id || h.node_id || h.id, name: node?.name || h.slug, release: h.release_no, streams: node?.streams ?? 0 }))
          }}
        />
      )}
      {restartToast && <SwitchToast o={restartToast} testId="restart-node-toast" onClose={() => setRestartToast(null)} />}

      <section className="space-y-2">
        <h4 className="text-sm font-medium">Last commands</h4>
        {!commands.length ? (
          <p className="text-xs text-muted">No commands yet.</p>
        ) : (
          <ul data-testid="host-commands" className="divide-y divide-line rounded-md border border-line">
            {commands.slice(0, 20).map((c) => (
              <CommandRow key={c.seq} c={c} me={user?.id} onReboot={deleted ? undefined : () => ask('reboot')} />
            ))}
          </ul>
        )}
      </section>

      <div>
        <Button variant="quiet" aria-expanded={showJobs} data-testid="host-jobs-toggle" onClick={() => setShowJobs(!showJobs)}>
          {showJobs ? 'Hide install history' : 'Install history'}
        </Button>
        {showJobs && (
          <div className="mt-2">
            <HostJobs hostId={h.id} />
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hosts without a node yet (installing, failed installs)
// ---------------------------------------------------------------------------

/** Hosts whose media node is not (yet) in the list: an install running or failed, or a node gone. */
export function PendingHosts({ hosts, info, onProgress, onManage }: { hosts: Host[]; info?: InstallInfo; onProgress: (h: Host) => void; onManage: (h: Host) => void }) {
  if (!hosts.length) return null
  return (
    <div data-testid="hosts-pending" className="-m-4 overflow-x-auto">
      <table className="sx-table compact [&_td]:whitespace-normal [&_th]:whitespace-normal w-full min-w-[720px]">
        <thead>
          <tr>
            <th className="px-4">Host</th>
            <th className="px-4">Address</th>
            <th className="px-4">State</th>
            <th className="px-4">Release</th>
            <th className="px-4" />
          </tr>
        </thead>
        <tbody>
          {hosts.map((h) => {
            const a = agentState(h)
            const state = h.installing ? 'Installing' : !h.node_id ? 'Not installed: the install failed or was stopped' : 'Its node is not in the list'
            return (
              <tr key={h.id} data-testid={`host-row-${h.id}`} data-agent={a.key}>
                <td className="px-4 py-1.5 align-top">
                  <p className="font-medium">{h.slug}</p>
                  <p className="font-mono text-xs text-muted">{h.hostname || h.id}</p>
                </td>
                <td className="px-4 py-1.5 align-top font-mono text-xs">{h.address || '—'}</td>
                <td className="px-4 py-1.5 align-top">
                  <span data-testid={`host-state-${h.id}`} className="flex items-center gap-2 text-xs">
                    <Tally state={h.installing ? 'busy' : 'error'} label={state} /> {state}
                  </span>
                </td>
                <td className="tabular px-4 py-1.5 align-top font-mono text-xs">
                  {h.release_no !== undefined ? `#${h.release_no}` : info?.release_no ? `→ #${info.release_no}` : '—'}
                </td>
                <td className="px-4 py-1.5 text-right align-top whitespace-nowrap">
                  <Button variant="quiet" data-testid={`host-progress-${h.id}`} onClick={() => onProgress(h)}>
                    Progress
                  </Button>
                  <Button variant="quiet" data-testid={`host-manage-${h.id}`} onClick={() => onManage(h)}>
                    Manage
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
